import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useContactModal } from "../../context/ContactModalContext";
import SalesLogo from "../../assets/Images/logo-sales.png";

const NAV = [
  {
    label: "Für Unternehmen",
    columns: [
      {
        heading: "Recruiting",
        items: [
          { label: "Vertrieb klassisch", to: "/sales/sales-klassisch" },
          { label: "Industrie Sales", to: "/sales/sales-industrie" },
          { label: "Healthcare Sales", to: "/sales/sales-healthcare" },
        ],
      },
      {
        heading: "Erfolg",
        items: [
          { label: "Referenzen", to: "/sales/referenzen" },
        ],
      },
    ],
  },
  {
    label: "Ressourcen",
    columns: [
      {
        heading: "Lesen",
        items: [{ label: "Einblicke", to: "/sales/insights" }],
      },
    ],
  },
  {
    label: "Über uns",
    columns: [
      {
        heading: "Unternehmen",
        items: [
          { label: "Unsere Geschichte", to: "/sales/geschichte" },
          { label: "Team", to: "/sales/team" },
        ],
      },
      {
        heading: "Präsenz",
        items: [
          { label: "Auszeichnungen", to: "/sales/awards" },
          { label: "Presse & Magazine", to: "/sales/magazine" },
        ],
      },
    ],
  },
];

export default function SalesNavbar() {
  const { openContactModal } = useContactModal() || {};
  const [openIndex, setOpenIndex] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileClosing, setMobileClosing] = useState(false);
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState({});
  const closeTimer = useRef(null);

  const openMenu = (i) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenIndex(i);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenIndex(null), 140); // small delay fixes "dropdown disappears"
  };

  const closeNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenIndex(null);
    handleCloseMobile();
  };

  const handleCloseMobile = () => {
    setMobileClosing(true);
    setTimeout(() => {
      setMobileOpen(false);
      setMobileClosing(false);
    }, 250);
  };

  const toggleMobileGroup = (label) => {
    setMobileGroupsOpen((prev) => ({
      ...prev,
      [label]: !(prev[label] ?? false),
    }));
  };

  return (
    <header className="sticky top-0 z-50 border-b p-2 border-gray-200 bg-white">
      <div className="container-app flex h-[4.5rem] md:h-20 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" aria-label="Repexo Sales Startseite">
          <img
            src={SalesLogo}
            alt="REPEXO Sales Logo"
            className="h-12 w-auto md:h-[4.35rem]"
          />
        </Link>

        {/* Center nav – desktop/tablet */}
        <nav className="hidden items-center gap-3 md:flex">
          <Link
            to="/sales"
            className="rounded-full px-4 py-2 text-[16px] font-medium text-gray-900 border border-transparent hover:border-gray-300 transition-all duration-200 ease-out"
          >
            Startseite
          </Link>
          {NAV.map((group, i) => {
            const isOpen = openIndex === i;

            return (
              <div
                key={group.label}
                className="relative"
                onMouseEnter={() => openMenu(i)}
                onMouseLeave={scheduleClose}
              >
                {/* Main item */}
                <button
                  type="button"
                  onFocus={() => openMenu(i)}
                  onBlur={scheduleClose}
                  className={[
                    "rounded-full px-4 py-2 text-[16px] font-medium text-gray-900",
                    "transition-all duration-200 ease-out",
                    "hover:border hover:border-gray-300",
                    isOpen
                      ? "border border-gray-300"
                      : "border border-transparent",
                  ].join(" ")}
                >
                  {group.label}
                </button>

                {/* Dropdown */}
                <div
                  onMouseEnter={() => openMenu(i)}
                  onMouseLeave={scheduleClose}
                  className={[
                    "absolute left-0 top-full pt-3",
                    isOpen
                      ? "pointer-events-auto opacity-100"
                      : "pointer-events-none opacity-0",
                    "transition-opacity duration-200 ease-out",
                  ].join(" ")}
                >
                  <div className="w-max min-w-0 rounded-2xl border border-gray-200 bg-white shadow-lg p-4">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      {group.columns.map((col, colIdx) => (
                        <div key={colIdx}>
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                            {col.heading}
                          </div>
                          <ul className="mt-0 space-y-0">
                            {col.items.map((item) => (
                              <li key={item.to}>
                                <Link
                                  to={item.to}
                                  onClick={closeNow}
                                  className="group flex items-center gap-2 py-2.5 text-base font-medium text-gray-900 transition-colors duration-200 ease-out hover:text-gray-600"
                                >
                                  <span>{item.label}</span>
                                  <ArrowRight className="h-3.5 w-3.5 shrink-0 -translate-x-1 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100" />
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Right controls */}
        <div className="flex items-center gap-4">
          {/* Desktop contact button */}
          <button
            type="button"
            onClick={() => openContactModal?.()}
            className="!hidden md:!block btn btn-outline text-gray-900 text-[15px]"
          >
            Jetzt Kontakt aufnehmen
          </button>

          {/* Mobile hamburger trigger */}
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-800 md:hidden"
            onClick={() => {
              if (mobileOpen) {
                handleCloseMobile();
              } else {
                setMobileOpen(true);
              }
            }}
            aria-label={mobileOpen ? "Navigation schließen" : "Navigation öffnen"}
          >
            {mobileOpen ? (
              <span className="text-xl leading-none">×</span>
            ) : (
              <span className="flex flex-col items-center justify-center gap-1">
                <span className="block h-[2px] w-4 bg-gray-800" />
                <span className="block h-[2px] w-4 bg-gray-800" />
                <span className="block h-[2px] w-4 bg-gray-800" />
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile nav panel as overlay (only below navbar) */}
      {mobileOpen && (
        <div
          className="fixed inset-x-0 top-[5.5rem] bottom-0 z-40 md:hidden bg-black/40"
          onClick={handleCloseMobile}
        >
          <div
            className={`absolute inset-x-0 top-0 border-t border-gray-200 bg-white shadow-[0_18px_50px_rgba(0,0,0,0.18)] ${
              mobileClosing ? "mobile-nav-exit" : "mobile-nav-enter"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="py-4 space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto px-4">
              <Link
                to="/sales"
                onClick={closeNow}
                className="block py-2 text-[15px] font-medium text-gray-900"
              >
                Startseite
              </Link>

              {NAV.map((group) => {
                const isGroupOpen = mobileGroupsOpen[group.label] ?? false;
                return (
                  <div key={group.label} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleMobileGroup(group.label)}
                      className="flex w-full items-center justify-between text-xs font-semibold uppercase tracking-[0.16em] text-gray-500"
                    >
                      <span>{group.label}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          isGroupOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div className={isGroupOpen ? "space-y-0.5" : "hidden"}>
                      {group.columns.flatMap((col) =>
                        col.items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={closeNow}
                            className="block py-2 text-[15px] font-medium text-gray-900"
                          >
                            {item.label}
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => {
                  openContactModal?.();
                  setMobileOpen(false);
                }}
                className="w-full btn btn-bg text-gray-900"
              >
                Kontakt
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}